/**
 * actionVisuals.js — single source of truth for action category styling.
 *
 * Used by:
 *   - pages/ActionCenter.jsx       (live action list + card)
 *   - pages/DoneActions.jsx        (Done page card / row tinting)
 *   - components/shared/ActionBadges.jsx  (badge primitives consume the maps)
 *
 * Adding a new action_category? Add an entry here and the visual treatment
 * propagates to every consumer.
 */
import {
  Users2, PackageCheck, ShieldAlert, TrendingUp, ArrowRightLeft,
  Zap, CloudLightning, Route, Factory,
} from 'lucide-react'

export function fmtPnl(v) {
  if (!v || v === 0) return '—'
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(3).replace(/\.?0+$/, '')}M`
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`
  return `$${v.toFixed(0)}`
}

export const CATEGORY_META = {
  LABOR_FLEX:               { icon: Users2,         color: 'text-cyan-400',    bg: 'bg-cyan-500/15',    border: 'border-cyan-500/30',    label: 'Reduce Staffing' },
  LABOR_SURGE_PREP:         { icon: TrendingUp,     color: 'text-sky-400',     bg: 'bg-sky-500/15',     border: 'border-sky-500/30',     label: 'Surge Prep' },
  PREDEPLOYMENT:            { icon: PackageCheck,   color: 'text-purple-400',  bg: 'bg-purple-500/15',  border: 'border-purple-500/30',  label: 'Stock Pre-Positioning' },
  ALLOCATION_HOLD:          { icon: ShieldAlert,    color: 'text-amber-400',   bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   label: 'Hold Allocation' },
  REALLOCATION_OPPORTUNITY: { icon: ArrowRightLeft, color: 'text-violet-400',  bg: 'bg-violet-500/15',  border: 'border-violet-500/30',  label: 'Redirect Inventory' },
  DC_YARD_CLEARANCE:        { icon: Zap,            color: 'text-red-400',     bg: 'bg-red-500/15',     border: 'border-red-500/30',     label: 'Clear DC Yard' },
  SURGE_WARNING:            { icon: CloudLightning, color: 'text-blue-400',    bg: 'bg-blue-500/15',    border: 'border-blue-500/30',    label: 'Surge Prep' },
  INBOUND_RISK:             { icon: Factory,        color: 'text-rose-400',    bg: 'bg-rose-500/15',    border: 'border-rose-500/30',    label: 'Inbound Risk' },
  LANE_RISK:                { icon: Route,          color: 'text-indigo-400',  bg: 'bg-indigo-500/15',  border: 'border-indigo-500/30',  label: 'Lane Risk' },
}

export const PILLAR_COLORS = {
  'Asset Liquidity':    { text: 'text-amber-400',   bg: 'bg-amber-500/15',   border: 'border-amber-500/30' },
  'Labor Preservation': { text: 'text-cyan-400',    bg: 'bg-cyan-500/15',    border: 'border-cyan-500/30' },
  'Process Velocity':   { text: 'text-purple-400',  bg: 'bg-purple-500/15',  border: 'border-purple-500/30' },
}

export const URGENCY_COLORS = {
  'Immediate (24h)':     { text: 'text-red-400',   bg: 'bg-red-500/15',   border: 'border-red-500/30' },
  'Proactive (48–96h)':  { text: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30' },
}

// Plain-English copy for each urgency_reason code emitted by the backend.
// Lets the badge tooltip explain Immediate-with-late-event situations like
// "Immediate because WOS is 1.2 wks (storm itself is 53h out)".
export const URGENCY_REASON_COPY = {
  event_imminent:    'Storm hits in the next 48 hours.',
  hourly_onset:      'Severity-4 onset is within 24 hours.',
  low_wos:           'Weeks-on-hand below 1.5 — near-stockout dominates timing.',
  promo_escalation:  'Active promotion lifts demand during the storm window.',
  trigger_imminent:  'Moderate weather (sev≥3) starts in the next 48h even though peak (sev≥4) is later.',
  planned:           'You have planning runway — act inside the 48–96h window.',
}

// Tailwind classes per lead-time tier — a quick visual signal beyond the
// numeric label (red for imminent, amber for soon, etc).
export const LEAD_TIME_TIER_CLASS = {
  past:    'text-slate-500 bg-white/5 border-white/10',
  now:     'text-red-400 bg-red-500/15 border-red-500/30',
  soon:    'text-amber-400 bg-amber-500/15 border-amber-500/30',
  planned: 'text-cyan-400 bg-cyan-500/15 border-cyan-500/30',
  far:     'text-slate-400 bg-white/5 border-white/10',
  unknown: 'text-slate-500 bg-white/5 border-white/10',
}
